export const authorizationRoles = [
  "guest",
  "independent_specialist",
  "client_owner",
  "client_manager",
  "vendor_owner",
  "vendor_manager",
  "brainics_recruiter",
  "administrator",
  "superadministrator",
] as const;

export type AuthorizationRole = (typeof authorizationRoles)[number];

export const assignableAuthorizationRoles = authorizationRoles.filter(
  (role): role is Exclude<AuthorizationRole, "guest"> => role !== "guest",
);

export type AssignableAuthorizationRole = (typeof assignableAuthorizationRoles)[number];

export const companyAuthorizationRoles = [
  "client_owner",
  "client_manager",
  "vendor_owner",
  "vendor_manager",
] as const satisfies readonly AssignableAuthorizationRole[];

export const platformAuthorizationRoles = [
  "brainics_recruiter",
  "administrator",
  "superadministrator",
] as const satisfies readonly AssignableAuthorizationRole[];

export const accessPermissions = [
  "account.self.read",
  "account.self.update",
  "company.create",
  "company.read",
  "company.update",
  "company.members.read",
  "company.members.manage",
  "company.roles.manage",
  "specialist.create",
  "specialist.read",
  "specialist.update",
  "specialist.moderate",
  "project.create",
  "project.read",
  "project.update",
  "project.access.manage",
  "vacancy.create",
  "vacancy.read",
  "vacancy.update",
  "vacancy.publish",
  "catalog.read",
  "matching.read",
  "matching.run",
  "matching.configure",
  "application.create",
  "application.read",
  "application.update",
  "pipeline.read",
  "pipeline.manage",
  "assignment.read",
  "assignment.manage",
  "timesheet.read",
  "timesheet.write",
  "timesheet.review",
  "timesheet.unlock",
  "document.read",
  "document.upload",
  "document.manage",
  "notification.read",
  "notification.manage",
  "export.create",
  "admin.dashboard.read",
  "admin.users.manage",
  "admin.companies.manage",
  "admin.specialists.manage",
  "admin.dictionaries.manage",
  "audit.read",
  "platform.roles.manage",
] as const;

export type AccessPermission = (typeof accessPermissions)[number];

export const authorizationRoleScopes = {
  guest: "public",
  independent_specialist: "self",
  client_owner: "company",
  client_manager: "company",
  vendor_owner: "company",
  vendor_manager: "company",
  brainics_recruiter: "platform",
  administrator: "platform",
  superadministrator: "platform",
} as const satisfies Record<AuthorizationRole, "public" | "self" | "company" | "platform">;

const accountSelfPermissions = ["account.self.read", "account.self.update"] as const;
const companyReadPermissions = ["company.read", "company.members.read"] as const;
const projectDeliveryPermissions = [
  "project.create",
  "project.read",
  "project.update",
  "vacancy.create",
  "vacancy.read",
  "vacancy.update",
  "vacancy.publish",
  "catalog.read",
  "matching.read",
  "matching.run",
  "pipeline.read",
  "pipeline.manage",
  "assignment.read",
  "assignment.manage",
  "timesheet.read",
  "timesheet.review",
  "document.read",
  "document.upload",
  "notification.read",
  "export.create",
] as const satisfies readonly AccessPermission[];
const vendorDeliveryPermissions = [
  "specialist.create",
  "specialist.read",
  "specialist.update",
  "vacancy.read",
  "application.create",
  "application.read",
  "application.update",
  "pipeline.read",
  "pipeline.manage",
  "assignment.read",
  "assignment.manage",
  "timesheet.read",
  "timesheet.write",
  "document.read",
  "document.upload",
  "notification.read",
  "export.create",
] as const satisfies readonly AccessPermission[];

const administratorPermissions = accessPermissions.filter(
  (permission) => permission !== "platform.roles.manage",
);

export const rolePermissionMatrix: Readonly<
  Record<AuthorizationRole, readonly AccessPermission[]>
> = {
  guest: [],
  independent_specialist: [
    ...accountSelfPermissions,
    "specialist.create",
    "specialist.read",
    "specialist.update",
    "vacancy.read",
    "application.create",
    "application.read",
    "application.update",
    "assignment.read",
    "timesheet.read",
    "timesheet.write",
    "document.read",
    "document.upload",
    "notification.read",
  ],
  client_owner: [
    ...accountSelfPermissions,
    "company.create",
    "company.read",
    "company.update",
    "company.members.read",
    "company.members.manage",
    "company.roles.manage",
    "specialist.read",
    ...projectDeliveryPermissions,
    "project.access.manage",
    "document.manage",
  ],
  client_manager: [
    ...accountSelfPermissions,
    ...companyReadPermissions,
    "specialist.read",
    ...projectDeliveryPermissions,
  ],
  vendor_owner: [
    ...accountSelfPermissions,
    "company.create",
    "company.read",
    "company.update",
    "company.members.read",
    "company.members.manage",
    "company.roles.manage",
    ...vendorDeliveryPermissions,
    "document.manage",
  ],
  vendor_manager: [
    ...accountSelfPermissions,
    ...companyReadPermissions,
    ...vendorDeliveryPermissions,
  ],
  brainics_recruiter: [
    ...accountSelfPermissions,
    ...companyReadPermissions,
    "specialist.read",
    "specialist.update",
    "project.read",
    "vacancy.read",
    "vacancy.update",
    "catalog.read",
    "matching.read",
    "matching.run",
    "application.read",
    "application.update",
    "pipeline.read",
    "pipeline.manage",
    "assignment.read",
    "assignment.manage",
    "timesheet.read",
    "document.read",
    "document.upload",
    "notification.read",
    "export.create",
  ],
  administrator: administratorPermissions,
  superadministrator: accessPermissions,
};

export function roleHasPermission(
  role: AuthorizationRole,
  permission: AccessPermission,
): boolean {
  return rolePermissionMatrix[role].includes(permission);
}

export function hasPermission(
  roles: readonly AuthorizationRole[],
  permission: AccessPermission,
): boolean {
  return roles.some((role) => roleHasPermission(role, permission));
}

export function permissionsForRoles(
  roles: readonly AuthorizationRole[],
): AccessPermission[] {
  const granted = new Set<AccessPermission>();

  for (const role of roles) {
    for (const permission of rolePermissionMatrix[role]) {
      granted.add(permission);
    }
  }

  return accessPermissions.filter((permission) => granted.has(permission));
}
